import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, todayStr, fmtDate, CYLINDERS } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { IndianRupee, Search } from "lucide-react";

function Cylinders({ canEdit }) {
  const [current, setCurrent] = useState([]);
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ cylinder_type: "DOM_14_2", price: "", effective_date: todayStr() });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/prices/current").then((r) => setCurrent(r.data));
    api.get("/prices").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.price) { toast.error("Enter a price"); return; }
    setBusy(true);
    try {
      await api.post("/prices", { ...form, price: parseFloat(form.price) });
      toast.success("Price saved");
      setForm({ ...form, price: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {current.map((c) => (
          <Card key={c.cylinder_type} className="p-4">
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="mt-1 kpi-number text-2xl font-semibold tabular-nums">{inr(c.price)}</div>
          </Card>
        ))}
      </div>
      {canEdit && (
        <Card className="p-5">
          <h2 className="text-lg font-semibold mb-3">Update a cylinder price</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div><Label>Cylinder</Label>
              <Select value={form.cylinder_type} onValueChange={(v) => setForm({ ...form, cylinder_type: v })}>
                <SelectTrigger data-testid="price-cylinder-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CYLINDERS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>New price (₹)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="price-amount-input" /></div>
            <div className="flex flex-col gap-1.5"><Label>Effective date</Label><DateField value={form.effective_date} onChange={(v) => setForm({ ...form, effective_date: v })} testid="price-date-input" className="w-full" /></div>
            <Button onClick={save} disabled={busy} data-testid="price-save-button">Save price</Button>
          </div>
        </Card>
      )}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><h2 className="text-lg font-semibold">Price history</h2></div>
        <Table>
          <TableHeader><TableRow><TableHead>Cylinder</TableHead><TableHead className="text-right">Price</TableHead><TableHead>Effective from</TableHead><TableHead>Set by</TableHead></TableRow></TableHeader>
          <TableBody>
            {history.map((h) => (
              <TableRow key={h.id}><TableCell>{h.label}</TableCell><TableCell className="text-right tabular-nums">{inr(h.price)}</TableCell><TableCell>{fmtDate(h.effective_date)}</TableCell><TableCell className="text-muted-foreground">{h.created_by}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function ArbPrices({ canEdit }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({});

  const load = () => api.get("/arb/items").then((r) => { setItems(r.data); const m = {}; r.data.forEach((i) => (m[i.id] = i.price)); setEdits(m); });
  useEffect(() => { load(); }, []);

  const savePrice = async (item) => {
    try {
      await api.put(`/arb/items/${item.id}`, { price: parseFloat(edits[item.id] || 0) });
      toast.success(`${item.name} price saved`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const filtered = items.filter((i) => (i.name + " " + (i.model || "")).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search ARB items" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="arb-price-search" />
        </div>
        <div className="text-sm text-muted-foreground">{items.length} items</div>
      </div>
      {canEdit && <p className="text-xs text-muted-foreground">Set the selling price for each item. Items with ₹0 need a price before they can be sold at a computed amount.</p>}
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto max-h-[560px]">
          <Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead>Model</TableHead><TableHead className="text-right">In stock</TableHead><TableHead className="text-right">Price</TableHead>{canEdit && <TableHead></TableHead>}</TableRow></TableHeader>
            <TableBody>
              {filtered.map((i) => (
                <TableRow key={i.id} data-testid={`arb-price-row-${i.id}`}>
                  <TableCell className="font-medium">{i.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{i.model}</TableCell>
                  <TableCell className="text-right tabular-nums">{i.stock}</TableCell>
                  {canEdit ? (
                    <TableCell className="text-right">
                      <Input type="number" value={edits[i.id] ?? ""} onChange={(e) => setEdits({ ...edits, [i.id]: e.target.value })} className="w-28 ml-auto text-right" data-testid={`arb-price-input-${i.id}`} />
                    </TableCell>
                  ) : (
                    <TableCell className="text-right tabular-nums">{inr(i.price)}</TableCell>
                  )}
                  {canEdit && <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => savePrice(i)} data-testid={`arb-price-save-${i.id}`}>Save</Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

export default function Prices() {
  const { user } = useAuth();
  const canEdit = user.role === "office";
  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><IndianRupee className="h-6 w-6 text-primary" /> Prices</h1>
        <p className="text-sm text-muted-foreground">Cylinder rates (effective-dated) and ARB item selling prices. {canEdit ? "You can edit these." : "View only."}</p>
      </div>
      <Tabs defaultValue="cylinders">
        <TabsList data-testid="prices-tabs">
          <TabsTrigger value="cylinders" data-testid="prices-tab-cylinders">Cylinders</TabsTrigger>
          <TabsTrigger value="arb" data-testid="prices-tab-arb">ARB items</TabsTrigger>
        </TabsList>
        <TabsContent value="cylinders" className="mt-4"><Cylinders canEdit={canEdit} /></TabsContent>
        <TabsContent value="arb" className="mt-4"><ArbPrices canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}
